import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function ShowerDoors() {
  const { t } = useTranslation('common');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const galleryImages = [
    {
      url: 'https://public.readdy.ai/ai/img_res/72d263ffe4e0c3a8271ac4c8e0140b8c.jpg',
      alt: 'Frameless Glass Shower Door'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=semi%20frameless%20shower%20door%20with%20metal%20frame%20elegant%20bathroom%20enclosure%20polished%20finish%20modern%20hardware%20clean%20design&width=800&height=600&seq=sd002&orientation=landscape',
      alt: 'Semi-Frameless Shower Door'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=sliding%20shower%20door%20space%20saving%20design%20smooth%20gliding%20mechanism%20modern%20bathroom%20glass%20panels%20contemporary%20hardware&width=800&height=600&seq=sd003&orientation=landscape',
      alt: 'Sliding Shower Door'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=pivot%20shower%20door%20elegant%20swinging%20design%20luxury%20bathroom%20clear%20glass%20chrome%20hinges%20sophisticated%20style&width=800&height=600&seq=sd004&orientation=landscape',
      alt: 'Pivot Shower Door'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=neo%20angle%20shower%20door%20corner%20installation%20space%20efficient%20design%20modern%20bathroom%20glass%20enclosure%20contemporary%20hardware&width=800&height=600&seq=sd005&orientation=landscape',
      alt: 'Neo-Angle Shower Door'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=walk%20in%20shower%20with%20glass%20panel%20open%20concept%20design%20modern%20bathroom%20frameless%20glass%20minimalist%20luxury%20style&width=800&height=600&seq=sd006&orientation=landscape',
      alt: 'Walk-In Shower Panel'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=steam%20shower%20door%20sealed%20enclosure%20luxury%20bathroom%20spa%20experience%20clear%20glass%20modern%20hardware%20premium%20design&width=800&height=600&seq=sd007&orientation=landscape',
      alt: 'Steam Shower Door'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=custom%20shower%20door%20unique%20design%20tailored%20bathroom%20solution%20elegant%20glass%20panels%20modern%20hardware%20personalized%20style&width=800&height=600&seq=sd008&orientation=landscape',
      alt: 'Custom Shower Door'
    }
  ];

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const goToPrevious = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentImageIndex((prev) => (prev === galleryImages.length - 1 ? 0 : prev + 1));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') goToPrevious();
    if (e.key === 'ArrowRight') goToNext();
    if (e.key === 'Escape') closeLightbox();
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=luxury%20modern%20bathroom%20with%20frameless%20glass%20shower%20door%20elegant%20design%20clear%20transparent%20glass%20chrome%20hardware%20contemporary%20spa%20like%20atmosphere%20clean%20minimalist%20style&width=1920&height=500&seq=sdhero&orientation=landscape"
            alt="Shower Doors Hero"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">{t('showerDoors.hero.title')}</h1>
          <p className="text-xl md:text-2xl mb-8">{t('showerDoors.hero.subtitle')}</p>
          <a
            href="/#contact"
            className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('showerDoors.hero.cta')}
          </a>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('showerDoors.overview.title')}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('showerDoors.overview.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-contrast-drop-2-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('showerDoors.overview.feature1.title')}</h3>
              <p className="text-gray-600">{t('showerDoors.overview.feature1.description')}</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-tools-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('showerDoors.overview.feature2.title')}</h3>
              <p className="text-gray-600">{t('showerDoors.overview.feature2.description')}</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-star-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('showerDoors.overview.feature3.title')}</h3>
              <p className="text-gray-600">{t('showerDoors.overview.feature3.description')}</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-brush-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('showerDoors.overview.feature4.title')}</h3>
              <p className="text-gray-600">{t('showerDoors.overview.feature4.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Styles Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('showerDoors.styles.title')}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('showerDoors.styles.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <div key={num} className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-layout-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {t(`showerDoors.styles.style${num}.title`)}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {t(`showerDoors.styles.style${num}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('showerDoors.gallery.title')}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('showerDoors.gallery.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                className="relative aspect-[4/3] overflow-hidden rounded-lg cursor-pointer group"
                onClick={() => openLightbox(index)}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                  <i className="ri-zoom-in-line text-white text-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line text-4xl"></i>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
            className="absolute left-4 text-white hover:text-gray-300 transition-colors cursor-pointer"
            aria-label="Previous"
          >
            <i className="ri-arrow-left-s-line text-5xl"></i>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            className="absolute right-4 text-white hover:text-gray-300 transition-colors cursor-pointer"
            aria-label="Next"
          >
            <i className="ri-arrow-right-s-line text-5xl"></i>
          </button>

          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={galleryImages[currentImageIndex].url}
              alt={galleryImages[currentImageIndex].alt}
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
            />
            <div className="text-center mt-4 text-white text-lg">
              {currentImageIndex + 1} / {galleryImages.length}
            </div>
          </div>
        </div>
      )}

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('showerDoors.benefits.title')}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('showerDoors.benefits.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <div key={num} className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-white"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {t(`showerDoors.benefits.benefit${num}.title`)}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {t(`showerDoors.benefits.benefit${num}.description`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('showerDoors.cta.title')}</h2>
          <p className="text-xl text-gray-600 mb-8">
            {t('showerDoors.cta.description')}
          </p>
          <a
            href="/#contact"
            className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-10 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('showerDoors.cta.button')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}